import React, { useEffect, useState, Suspense } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/useAuth';
import { LoadingState } from '../../../shared/components/ui/LoadingState';
import { ErrorBoundary } from '../../../shared/components/ErrorBoundary';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: string;
  requiredOperatorType?: string;
  requireVerification?: boolean;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
  requiredOperatorType,
  requireVerification = false,
}) => {
  const { user, isAuthenticated, loading, redirectToLogin } = useAuth();
  const location = useLocation();
  const [hasCheckedAuth, setHasCheckedAuth] = useState(false);

  useEffect(() => {
    // Only check auth once when loading is complete
    if (!loading && !hasCheckedAuth) {
      setHasCheckedAuth(true);
      
      // If not authenticated after loading is complete, redirect to login
      if (!isAuthenticated) {
        redirectToLogin('Please sign in to access this page', location.pathname);
      }
    }
  }, [
    loading,
    isAuthenticated,
    hasCheckedAuth,
    redirectToLogin,
    location.pathname,
  ]);

  // Show loading state only during initial auth loading
  if (loading) {
    return <LoadingState message="Loading application..." />;
  }

  // If not authenticated, render nothing (redirect will happen)
  if (!isAuthenticated) {
    return null;
  }

  // Check role if required
  if (requiredRole && user?.type !== requiredRole) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Check operator type if required
  if (requiredOperatorType && user?.operator_type !== requiredOperatorType) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Check verification if required
  if (requireVerification && !user?.is_verified) {
    return <Navigate to="/verification-required" replace />;
  }

  // If all checks pass, render the protected content
  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingState message="Loading page..." />}>
        {children}
      </Suspense>
    </ErrorBoundary>
  );
};
